import {
  TbChecklist,
  TbEyeOff,
  TbLock,
  TbPlayerPause,
  TbPlayerPlay,
  TbQuote,
  TbRobotOff,
  TbShieldCheck,
  TbUserShield,
} from "solid-icons/tb";
import { createSignal, For, Show } from "solid-js";
import { GmailIcon } from "~/components/knowledge/pages/ScopePickerEmptyState";
import { TESTIMONIALS, type Testimonial } from "~/components/marketing/content";
import { COMPLIANCE_BADGES } from "~/components/marketing/MarketingFooter";
import { ApproveFoot, DoneRow, NeedsCard, NeedsGroup } from "~/components/marketing/mockups";
import { CardArrow, Eyebrow, LINK_CARD, SectionTitle } from "~/components/marketing/ui";
import { useI18n } from "~/i18n";
import type { TranslationKey } from "~/i18n/translations";
import "~/components/marketing/home/home.css";
import "~/components/marketing/mockups/mockups.css";

const FEATURED = TESTIMONIALS.find((item) => item.featured) ?? TESTIMONIALS[0];
const WALL = TESTIMONIALS.filter((item) => item !== FEATURED);

/**
 * One large quote with its result pulled out, beside a wall of the rest that
 * drifts on its own; hovering or the pause button stops it.
 */
export const TestimonialsSection = () => {
  const { t } = useI18n();
  const [paused, setPaused] = createSignal(false);
  return (
    <section aria-labelledby="testimonials-title" class="px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
      <div class="mx-auto max-w-7xl">
        <div class="flex items-end justify-between gap-6">
          <div class="flex flex-col gap-5">
            <Eyebrow label={t("marketing.home.testimonials.eyebrow")} id="testimonials-title" />
            <SectionTitle id="testimonials-title" text={t("marketing.home.testimonials.title")} />
          </div>
          <button
            type="button"
            aria-pressed={paused()}
            aria-label={t("marketing.home.testimonials.pause")}
            title={t("marketing.home.testimonials.pause")}
            onClick={() => setPaused(!paused())}
            class="grid size-10 shrink-0 place-items-center rounded-full border border-primary-border bg-tile text-secondary-text transition-colors hover:text-primary-text focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none motion-reduce:hidden"
          >
            <Show when={paused()} fallback={<TbPlayerPause class="size-4" aria-hidden="true" />}>
              <TbPlayerPlay class="size-4" aria-hidden="true" />
            </Show>
          </button>
        </div>
        <div class="mt-10 grid gap-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:grid-rows-[minmax(0,1fr)] lg:motion-safe:h-120 lg:motion-reduce:items-start">
          <FeaturedQuote item={FEATURED} />
          <div
            class="qw -mx-4 grid gap-3 sm:-mx-6 lg:mx-0 lg:min-h-0 lg:grid-cols-2 lg:grid-rows-[minmax(0,1fr)]"
            classList={{ "qw-paused": paused() }}
          >
            <QuoteColumn items={WALL.filter((_, i) => i % 2 === 0)} />
            <QuoteColumn items={WALL.filter((_, i) => i % 2 === 1)} reverse />
          </div>
        </div>
      </div>
    </section>
  );
};

const Avatar = (props: { item: Testimonial; class: string }) => (
  <Show
    when={props.item.photo}
    fallback={
      <span
        aria-hidden="true"
        class={`mk-ink grid shrink-0 place-items-center rounded-full bg-(--c)/12 font-semibold ring-1 ring-(--c)/25 dark:bg-(--c)/20 ${props.class}`}
      >
        {props.item.name
          .split(" ")
          .map((part) => part[0])
          .join("")}
      </span>
    }
  >
    {(photo) => <img src={photo()} alt="" loading="lazy" class={`shrink-0 rounded-full object-cover ${props.class}`} />}
  </Show>
);

const CompanyMark = (props: { item: Testimonial; large?: boolean }) => (
  <Show
    when={props.item.logo}
    fallback={
      <span class="flex min-w-0 items-center gap-2">
        <span
          aria-hidden="true"
          class="mk-ink grid shrink-0 place-items-center rounded-md bg-(--c)/12 font-bold ring-1 ring-(--c)/20 dark:bg-(--c)/20"
          classList={{ "size-6 text-xs": !props.large, "size-8 text-sm": props.large }}
        >
          {props.item.company[0]}
        </span>
        <span class="truncate font-semibold" classList={{ "text-sm": !props.large }}>
          {props.item.company}
        </span>
      </span>
    }
  >
    {(logo) => (
      <img
        src={logo()}
        alt={props.item.company}
        loading="lazy"
        class="w-auto"
        classList={{ "h-5": !props.large, "h-7": props.large }}
      />
    )}
  </Show>
);

const ContextTag = (props: { text?: string }) => (
  <Show when={props.text}>
    {(text) => (
      <span class="shrink-0 rounded-full bg-primary-border/50 px-2 py-0.5 text-xs whitespace-nowrap text-secondary-text dark:bg-primary-border">
        {text()}
      </span>
    )}
  </Show>
);

const FeaturedQuote = (props: { item: Testimonial }) => (
  <figure
    class="relative flex flex-col overflow-hidden rounded-3xl border border-primary-border bg-tile p-6 sm:p-8"
    style={{ "--c": props.item.color }}
  >
    <div
      aria-hidden="true"
      class="pointer-events-none absolute inset-0 bg-[radial-gradient(var(--color-primary-border)_1px,transparent_1px)] mask-[radial-gradient(ellipse_at_top_right,black,transparent_65%)] bg-size-[20px_20px]"
    />
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -inset-e-24 -top-24 size-72 rounded-full bg-(--c)/12 blur-3xl"
    />
    <div class="relative flex items-center justify-between gap-3">
      <CompanyMark item={props.item} large />
      <ContextTag text={props.item.context} />
    </div>
    <TbQuote class="relative mt-8 size-9 text-accent" aria-hidden="true" />
    <blockquote class="relative mt-2 flex-1 text-2xl leading-snug font-medium tracking-tight text-pretty sm:text-3xl">
      {props.item.quote}
    </blockquote>
    <Show when={props.item.highlight}>
      {(highlight) => (
        <p class="relative mt-8 flex items-baseline gap-2 self-start rounded-2xl bg-accent/12 px-4 py-2.5 ring-1 ring-accent/25">
          <span class="text-3xl font-semibold tracking-tight text-accent-text">{highlight().value}</span>
          <span class="text-sm font-medium text-secondary-text">{highlight().label}</span>
        </p>
      )}
    </Show>
    <figcaption class="relative mt-6 flex items-center gap-3 border-t border-primary-border pt-5">
      <Avatar item={props.item} class="size-12 text-base" />
      <span class="min-w-0 text-sm">
        <span class="block text-base font-semibold">{props.item.name}</span>
        <span class="block text-secondary-text">
          {props.item.role}, {props.item.company}
        </span>
      </span>
    </figcaption>
  </figure>
);

const QuoteCard = (props: { item: Testimonial }) => (
  <figure
    class="flex w-72 flex-col gap-4 rounded-2xl border border-primary-border bg-tile p-5 shadow-sm shadow-black/3 sm:w-80 lg:w-full dark:shadow-none"
    style={{ "--c": props.item.color }}
  >
    <div class="flex items-center justify-between gap-3">
      <CompanyMark item={props.item} />
      <ContextTag text={props.item.context} />
    </div>
    <blockquote class="leading-relaxed">{props.item.quote}</blockquote>
    <Show when={props.item.highlight}>
      {(highlight) => (
        <p class="-mt-1 self-start rounded-full bg-accent/12 px-2.5 py-1 text-xs text-secondary-text ring-1 ring-accent/25">
          <span class="font-semibold text-accent-text">{highlight().value}</span> {highlight().label}
        </p>
      )}
    </Show>
    <figcaption class="mt-auto flex items-center gap-3">
      <Avatar item={props.item} class="size-9 text-xs" />
      <span class="min-w-0 text-sm leading-tight">
        <span class="block font-semibold">{props.item.name}</span>
        <span class="block text-secondary-text">{props.item.role}</span>
      </span>
    </figcaption>
  </figure>
);

/** One column of the wall (a row below the large breakpoint), its cards listed twice for the loop. */
const QuoteColumn = (props: { items: Testimonial[]; reverse?: boolean }) => (
  <div
    class="qw-col overflow-hidden mask-[linear-gradient(to_right,transparent,black_6%,black_94%,transparent)] px-4 sm:px-6 lg:mask-[linear-gradient(to_bottom,transparent,black_10%,black_90%,transparent)] lg:px-0"
    classList={{ "qw-reverse": props.reverse }}
    style={{ "--qw-duration": `${props.items.length * 14}s` }}
  >
    <div class="qw-track flex w-max py-1 lg:w-auto lg:flex-col lg:py-0">
      <ul class="flex shrink-0 gap-3 pe-3 lg:flex-col lg:pe-0 lg:pb-3">
        <For each={props.items}>
          {(item) => (
            <li class="flex shrink-0">
              <QuoteCard item={item} />
            </li>
          )}
        </For>
      </ul>
      <ul aria-hidden="true" inert class="qw-dup flex shrink-0 gap-3 pe-3 lg:flex-col lg:pe-0 lg:pb-3">
        <For each={props.items}>
          {(item) => (
            <li class="flex shrink-0">
              <QuoteCard item={item} />
            </li>
          )}
        </For>
      </ul>
    </div>
  </div>
);

const TILES: { icon: typeof TbLock; color: string; title: TranslationKey; body: TranslationKey[]; href: string }[] = [
  {
    icon: TbRobotOff,
    color: "#1d9e75",
    title: "marketing.home.trust.no_bot",
    body: ["marketing.features.live.no_bot_body"],
    href: "/compare",
  },
  {
    icon: TbLock,
    color: "#d97706",
    title: "marketing.home.trust.encrypted",
    body: ["marketing.features.trust.encryption_body"],
    href: "/privacy#integrations",
  },
  {
    icon: TbEyeOff,
    color: "#db2777",
    title: "marketing.home.trust.no_training",
    body: ["marketing.features.trust.training_body"],
    href: "/privacy#responsible-ai",
  },
  {
    icon: TbUserShield,
    color: "#7c3aed",
    title: "marketing.home.trust.private",
    body: ["marketing.features.trust.private_body", "marketing.features.trust.audit_body"],
    href: "/changelog/organization-audit-log",
  },
];

const TILE = "relative overflow-hidden rounded-3xl border border-primary-border bg-tile p-6";

/** A soft tint of the promise's own hue: colour to tell the tiles apart, quiet enough not to compete. */
const IconTile = (props: { icon: typeof TbLock; color: string }) => (
  <span
    class="mk-ink grid size-11 shrink-0 place-items-center rounded-xl bg-(--c)/12 ring-1 ring-(--c)/20 dark:bg-(--c)/20"
    style={{ "--c": props.color }}
  >
    <props.icon class="size-5" aria-hidden="true" />
  </span>
);

/**
 * The trust promises as a bento: the approval moment as the large tile, the
 * four standing guarantees around it, and the certifications as a band below.
 * It renders on the homepage and the executives page.
 */
export const TrustSection = () => {
  const { t } = useI18n();
  return (
    <section
      aria-labelledby="trust-title"
      class="border-y border-primary-border bg-panel px-4 py-20 sm:px-6 sm:py-28 lg:px-8"
    >
      <div class="mx-auto max-w-7xl">
        <div class="flex flex-col gap-5">
          <Eyebrow label={t("marketing.home.trust.eyebrow")} id="trust-title" />
          <SectionTitle id="trust-title" text={t("marketing.home.trust.title")} />
        </div>
        <ul class="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:grid-rows-2">
          <li class="sm:col-span-2 lg:row-span-2" style={{ "--c": "#3049cf" }}>
            <a href="/features#trust" class={`${TILE} ${LINK_CARD} flex h-full flex-col gap-6 lg:p-8`}>
              <div
                aria-hidden="true"
                class="pointer-events-none absolute -inset-e-24 -top-24 size-72 rounded-full bg-brand/8 blur-3xl"
              />
              <ApprovalCard />
              <div class="relative flex items-start gap-4">
                <IconTile icon={TbChecklist} color="#3049cf" />
                <div class="flex flex-col gap-1.5">
                  <h3 class="flex items-center gap-1.5 text-xl font-semibold">
                    {t("marketing.home.trust.approve")}
                    <CardArrow />
                  </h3>
                  <p class="leading-relaxed text-secondary-text">{t("marketing.home.offers.trust")}</p>
                </div>
              </div>
            </a>
          </li>
          <For each={TILES}>
            {(tile) => (
              <li style={{ "--c": tile.color }}>
                <a href={tile.href} class={`${TILE} ${LINK_CARD} flex h-full flex-col gap-4`}>
                  <IconTile icon={tile.icon} color={tile.color} />
                  <h3 class="text-lg leading-snug font-semibold">
                    {t(tile.title)}
                    <span class="whitespace-nowrap">
                      {" "}
                      <CardArrow />
                    </span>
                  </h3>
                  <p class="-mt-2 text-sm leading-relaxed text-secondary-text">
                    {tile.body.map((key) => t(key)).join(" ")}
                  </p>
                </a>
              </li>
            )}
          </For>
          <li class={`${TILE} sm:col-span-2 lg:col-span-4`}>
            <a
              href="https://trust.chief.bot"
              target="_blank"
              rel="noopener noreferrer"
              class="group flex flex-col gap-6 rounded-xl focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none md:flex-row md:items-center"
            >
              <span class="flex items-start gap-4 md:flex-1">
                <IconTile icon={TbShieldCheck} color="#0d9488" />
                <span class="flex flex-col gap-1.5">
                  <span class="text-lg font-semibold">{t("marketing.home.trust.certs")}</span>
                  <span class="text-sm text-secondary-text">{t("marketing.features.trust.certs_body")}</span>
                </span>
              </span>
              <span class="flex flex-wrap items-center gap-4">
                <For each={COMPLIANCE_BADGES}>
                  {(badge) => (
                    <img
                      src={badge.src}
                      alt={t(badge.label)}
                      loading="lazy"
                      class="h-20 w-auto transition-transform duration-200 group-hover:-translate-y-0.5"
                    />
                  )}
                </For>
              </span>
              <span class="text-sm font-semibold text-brand-text group-hover:underline md:ms-4">
                {t("marketing.home.trust.trust_center")} <span aria-hidden="true">→</span>
              </span>
            </a>
          </li>
        </ul>
      </div>
    </section>
  );
};

/** An approval as Chief asks for it under Needs you, beside a send that already landed. */
const ApprovalCard = () => {
  const { t } = useI18n();
  return (
    <div aria-hidden="true" class="mk-app relative m-auto flex w-full max-w-md flex-col gap-2 select-none">
      <div class="rounded-lg border border-primary-border bg-panel p-1 opacity-70 shadow-sm">
        <DoneRow title={t("marketing.mock.exec.slack")} where={t("marketing.mock.exec.sent")} />
      </div>
      <NeedsGroup
        class="bg-panel shadow-xl shadow-black/5 dark:shadow-black/40"
        icon={<GmailIcon class="size-3" />}
        label={t("marketing.mock.exec.gmail_drafts")}
        count={1}
      >
        <NeedsCard title={t("marketing.mock.exec.email")} foot={<ApproveFoot />}>
          <div class="mt-1.5 flex flex-col gap-1 text-xs leading-snug text-secondary-text">
            <p>{t("marketing.mock.roundtrip.summary1")}</p>
            <p>{t("marketing.mock.roundtrip.summary2")}</p>
          </div>
        </NeedsCard>
      </NeedsGroup>
    </div>
  );
};
